import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    DateSelectorDynamicStyle,
    CalendarCardConstantType
} from "./type";
import {
    DATE_SELECTOR_WEEK_DAY_COUNT
} from "./util";
import type {
    Mutable
} from "../../types";
import {
    getCornerStyle
} from "../../helpers/theme/corner";

const stylesheet = StyleSheet.create({
    nextPrevToolContainer: {
        justifyContent: "space-between",
        flexDirection: "row",
        alignItems: "center"
    },
    weekNumberContainer: {
        justifyContent: "center",
        alignItems: "center"
    },
    weekDayContainer: {
        justifyContent: "center",
        alignItems: "center"
    },
    captionContainer: {
        justifyContent: "center",
        flexDirection: "row",
        alignItems: "center",
        flexShrink: 1
    },
    monthContainer: {
        flexDirection: "column"
    },
    contentContainer: {
        flexDirection: "column"
    },
    weeksContainer: {
        flexDirection: "column"
    },
    liveTitle: {
        pointerEvents: "none",
        position: "absolute",
        overflow: "hidden",
        opacity: 0
    },
    monthsContainer: {
        flexDirection: "row"
    },
    weekContainer: {
        flexDirection: "row"
    },
    title: {
        textAlign: "center",
        flexShrink: 1
    },
    weekNumberTitle: {
        textAlign: "center",
        fontVariant: [
            "tabular-nums"
        ]
    },
    weekDayTitle: {
        textAlign: "center"
    },
    container: {
        alignSelf: "center"
    },
    measureContainer: {
        maxWidth: "100%",
        flexShrink: 1,
        minWidth: 0
    },
    measureProbe: {
        maxWidth: "100%",
        height: 0
    }
});

export const getCalendarCardStyle = ({
    weekColumnWidth = 0,
    controlHeights,
    monthCount = 1,
    shapeConfigs,
    isCardShown,
    isStretched,
    radiuses,
    borders,
    spaces,
    colors
}: CalendarCardConstantType): ViewStyle => {
    if(isStretched) {
        return {
            alignSelf: "stretch"
        };
    }

    const contentWidth = (controlHeights.medium * DATE_SELECTOR_WEEK_DAY_COUNT + weekColumnWidth) * monthCount + spaces.spacingLg * (monthCount - 1);

    if(!isCardShown) {
        return {
            width: contentWidth
        };
    }

    const inset = spaces.spacingMd + borders.line;

    return {
        backgroundColor: colors.content.container.raised,
        borderColor: colors.content.border.subtle,
        width: contentWidth + inset * 2,
        ...getCornerStyle({
            radius: radiuses.surface,
            shapeConfigs
        }),
        padding: spaces.spacingMd,
        borderWidth: borders.line
    };
};

export const useStyles = ({
    weekColumnWidth,
    controlHeights,
    shapeConfigs,
    isCardShown,
    isStretched,
    monthCount,
    radiuses,
    borders,
    spaces,
    colors
}: DateSelectorDynamicStyle) => {
    const styles = {
        nextPrevToolContainer: {
            marginBottom: spaces.spacingXs
        } as Mutable<ViewStyle>,
        weekNumberContainer: {
            height: controlHeights.medium,
            width: weekColumnWidth
        } as Mutable<ViewStyle>,
        headerSpacer: {
            height: controlHeights.medium,
            width: controlHeights.medium
        } as Mutable<ViewStyle>,
        blankDay: (isStretched ? {
            height: controlHeights.medium,
            flexBasis: 0,
            flexGrow: 1
        } : {
            height: controlHeights.medium,
            width: controlHeights.medium
        }) as Mutable<ViewStyle>,
        container: getCalendarCardStyle({
            weekColumnWidth,
            controlHeights,
            shapeConfigs,
            isCardShown,
            isStretched,
            monthCount,
            radiuses,
            borders,
            spaces,
            colors
        }) as Mutable<ViewStyle>,
        weekDayContainer: isStretched ? {
            height: controlHeights.small,
            flexBasis: 0,
            flexGrow: 1
        } as Mutable<ViewStyle> : {
            height: controlHeights.small,
            width: controlHeights.medium
        } as Mutable<ViewStyle>,
        weekNumberHeaderContainer: {
            height: controlHeights.small,
            width: weekColumnWidth
        } as Mutable<ViewStyle>,
        monthContainer: (isStretched ? {
            flexBasis: 0,
            flexGrow: 1,
            minWidth: 0
        } : {}) as Mutable<ViewStyle>,
        monthsContainer: {
            columnGap: spaces.spacingLg
        } as Mutable<ViewStyle>,
        captionContainer: {
            columnGap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        contentContainer: {
            rowGap: spaces.spacingXxs
        } as Mutable<ViewStyle>,
        weeksContainer: {
            rowGap: spaces.spacingXxs
        } as Mutable<ViewStyle>
    };

    return styles;
};
export default stylesheet;
